import { t as createLogger } from "./app.shared-CmP4XLF_.js";

var slider_controls_default = (() => {
    if (customElements.get("slider-controls")) return;
    customElements.define("slider-controls", class SliderControls extends HTMLElement {
        static get observedAttributes() {
            return [ "data-slider-target", "data-slider-targets" ];
        }
        constructor() {
            super();
            this._rawLogger = createLogger("slider-controls");
            this.isDebugLogEnabled = false;
            this.slider = null;
            this.sliders = [];
            this.sliderStates = new WeakMap;
            this.prev = null;
            this.next = null;
            this._activityObserver = null;
            this._onPrevClick = this._onPrevClick.bind(this);
            this._onNextClick = this._onNextClick.bind(this);
            this._onSliderState = this._onSliderState.bind(this);
            this._onRefresh = this._onRefresh.bind(this);
        }
        log(topic, message, detail) {
            if (this.isDebugLogEnabled) this._rawLogger(topic, message, detail);
        }
        connectedCallback() {
            this.isDebugLogEnabled = this.hasAttribute("data-debug-enabled");
            this.prev = this.querySelector("[data-previous]");
            this.next = this.querySelector("[data-next]");
            this.log("lifecycle", "connected", {
                sliderTarget: this.dataset.sliderTarget || null,
                sliderTargets: this.dataset.sliderTargets || null
            });
            this.prev?.addEventListener("click", this._onPrevClick);
            this.next?.addEventListener("click", this._onNextClick);
            this.addEventListener("slider-controls:refresh", this._onRefresh);
            this._bindTargetSliders();
        }
        disconnectedCallback() {
            this.prev?.removeEventListener("click", this._onPrevClick);
            this.next?.removeEventListener("click", this._onNextClick);
            this.removeEventListener("slider-controls:refresh", this._onRefresh);
            this._unbindTargetSliders();
        }
        _onPrevClick() {
            const activeSlider = this._getActiveSlider();
            if (!activeSlider) return;
            this.log("events", "previous click", {
                activeSlider: activeSlider.dataset.sliderId || null
            });
            activeSlider.dispatchEvent(new CustomEvent("slider:previous"));
        }
        _onNextClick() {
            const activeSlider = this._getActiveSlider();
            if (!activeSlider) return;
            this.log("events", "next click", {
                activeSlider: activeSlider.dataset.sliderId || null
            });
            activeSlider.dispatchEvent(new CustomEvent("slider:next"));
        }
        _onSliderState(event) {
            const slider = event.currentTarget;
            const detail = event.detail || {};
            this.sliderStates.set(slider, detail);
            this.log("events", "slider state event", {
                sourceSlider: slider?.dataset?.sliderId || null,
                controlsVisible: detail.controlsVisible,
                canGoPrev: detail.canGoPrev,
                canGoNext: detail.canGoNext
            });
            if (slider !== this._getActiveSlider()) return;
            this._applyState(detail);
        }
        _onRefresh() {
            this._bindTargetSliders();
        }
        refreshState() {
            this._syncActiveSliderState();
        }
        _applyState(detail = {}) {
            let controlsVisible = detail.controlsVisible === true;
            const canGoPrev = controlsVisible && detail.canGoPrev === true;
            const canGoNext = controlsVisible && detail.canGoNext === true;
            if (!canGoPrev && !canGoNext) controlsVisible = false;
            this.log("ui", "apply controls state", {
                activeSlider: this.slider?.dataset?.sliderId || null,
                controlsVisible: controlsVisible,
                canGoPrev: canGoPrev,
                canGoNext: canGoNext
            });
            const updateAriaHidden = (el, isVisible) => {
                if (!el) return;
                const val = isVisible ? "false" : "true";
                if (el.getAttribute("aria-hidden") !== val) el.setAttribute("aria-hidden", val);
            };
            updateAriaHidden(this, controlsVisible);
            if (this.prev) {
                if (this.prev.hasAttribute("disabled") === canGoPrev) this.prev.toggleAttribute("disabled", !canGoPrev);
                updateAriaHidden(this.prev, canGoPrev);
            }
            if (this.next) {
                if (this.next.hasAttribute("disabled") === canGoNext) this.next.toggleAttribute("disabled", !canGoNext);
                updateAriaHidden(this.next, canGoNext);
            }
        }
        _bindTargetSliders() {
            this._unbindTargetSliders();
            this.sliders = this._resolveTargetSliders();
            this.log("lifecycle", "bind target sliders", {
                count: this.sliders.length,
                ids: this.sliders.map(slider => slider.dataset.sliderId || null)
            });
            if (!this.sliders.length) {
                this._applyState();
                return;
            }
            this.sliders.forEach(slider => {
                slider.addEventListener("slider:init", this._onSliderState);
                slider.addEventListener("slider:change", this._onSliderState);
                slider.addEventListener("slider:controls-state", this._onSliderState);
            });
            this._syncActiveSliderState();
        }
        _unbindTargetSliders() {
            this.sliders.forEach(slider => {
                slider.removeEventListener("slider:init", this._onSliderState);
                slider.removeEventListener("slider:change", this._onSliderState);
                slider.removeEventListener("slider:controls-state", this._onSliderState);
            });
            this.sliders = [];
            this.slider = null;
            this._applyState();
        }
        _resolveTargetSliders() {
            const scope = this.closest(".shopify-section") || document;
            const explicitTargets = (this.dataset.sliderTargets || "").split(",").map(value => value.trim()).filter(Boolean);
            if (explicitTargets.length) return explicitTargets.map(targetId => scope.querySelector(`slider-component[data-slider-id="${targetId}"]`)).filter(Boolean);
            const explicitTarget = this.dataset.sliderTarget;
            if (explicitTarget) {
                const slider = scope.querySelector(`slider-component[data-slider-id="${explicitTarget}"]`);
                return slider ? [ slider ] : [];
            }
            const parentSlider = this.closest("slider-component");
            return parentSlider ? [ parentSlider ] : [];
        }
        _getActiveSlider() {
            if (!this.sliders.length) return null;
            if (this.dataset.sliderTarget) return this.sliders.find(slider => slider.dataset.sliderId === this.dataset.sliderTarget) || this.sliders[0];
            const flaggedSlider = this.sliders.find(slider => slider.dataset.rpActiveSlider === "true");
            if (flaggedSlider) return flaggedSlider;
            return this.sliders.find(slider => {
                const panel = slider.closest("[data-rp-panel]");
                return panel ? panel.getAttribute("aria-hidden") !== "true" : true;
            }) || this.sliders[0];
        }
        _syncActiveSliderState() {
            this.slider = this._getActiveSlider();
            this.log("lifecycle", "sync active slider", {
                activeSlider: this.slider?.dataset?.sliderId || null,
                available: this.sliders.map(slider => slider.dataset.sliderId || null)
            });
            if (!this.slider) {
                this._applyState();
                return;
            }
            const knownState = this.sliderStates.get(this.slider);
            if (knownState) this._applyState(knownState);
            const activeSlider = this.slider;
            activeSlider.dispatchEvent(new CustomEvent("slider:get-state", {
                detail: {
                    callback: state => {
                        const nextState = state || {};
                        this.sliderStates.set(activeSlider, nextState);
                        if (this.slider === activeSlider) this._applyState(nextState);
                    }
                }
            }));
        }
    });
})();